import { useMemo, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import Button from "../../components/Button";
import { ICON_STROKE } from "../../components/icons/icon-tokens";
import { PopupModal } from "../../components/Modal";
import {
  useConversationHistory,
  usePricingConversationHistory,
} from "../hooks/useConversationHistory";

type ChatMode = "chat" | "agent";

interface ConversationSidebarProps {
  currentConversationId?: string;
  onConversationChange?: (conversationId: string, mode: ChatMode) => void;
  onNewChat?: () => void;
}

/**
 * `.a-rail` — history rail used in the expanded assistant overlay.
 * Ported from the Claude-Design "Autumn Assistant" mock:
 *   - 280px width (was 260)
 *   - translucent `rgba(247,245,242,0.65)` background + `backdrop-blur(8px)`
 *     so the dimmed dashboard behind the modal subtly shows through
 *   - 1px hairline on the right edge (`var(--d-rule)`)
 *   - `.a-newchat` button (surface bg, hairline, lucide Plus icon)
 *   - `.a-rail-search` input (lucide Search, "Search conversations" placeholder)
 *   - `.a-rail-item` list rows with title (Geist) + mono meta ("Chat · Date")
 *   - active row pulled out as a solid surface card; hover tints lightly
 */
export function ConversationSidebar({
  currentConversationId,
  onConversationChange,
  onNewChat,
}: ConversationSidebarProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteSource, setConfirmDeleteSource] =
    useState<ChatMode | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    conversations: chatConversations,
    isLoading: isChatLoading,
    deleteConversation: deleteChatConversation,
  } = useConversationHistory();
  const {
    conversations: pricingConversations,
    isLoading: isAgentLoading,
    deleteConversation: deleteAgentConversation,
  } = usePricingConversationHistory();

  const isLoading = isChatLoading || isAgentLoading;

  const conversations = useMemo(() => {
    const chat = chatConversations.map((c) => ({
      id: c.id,
      title: c.title,
      lastMessageAt: c.lastMessageAt,
      source: "chat" as ChatMode,
    }));
    const agent = pricingConversations.map((c) => ({
      id: c.chatId,
      title: c.preview,
      lastMessageAt: c.updatedAt,
      source: "agent" as ChatMode,
    }));
    const merged = [...chat, ...agent].sort(
      (a, b) =>
        new Date(b.lastMessageAt).getTime() -
        new Date(a.lastMessageAt).getTime(),
    );
    if (!searchQuery.trim()) return merged;
    const q = searchQuery.trim().toLowerCase();
    return merged.filter((c) => c.title.toLowerCase().includes(q));
  }, [chatConversations, pricingConversations, searchQuery]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) return "Today";
    if (diffInHours < 48) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const handleDeleteClick = (
    conversationId: string,
    source: ChatMode,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setConfirmDeleteId(conversationId);
    setConfirmDeleteSource(source);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId || !confirmDeleteSource) return;

    setDeletingId(confirmDeleteId);
    setConfirmDeleteId(null);

    try {
      if (confirmDeleteSource === "agent") {
        await deleteAgentConversation(confirmDeleteId);
      } else {
        await deleteChatConversation(confirmDeleteId);
      }

      if (confirmDeleteId === currentConversationId) {
        onNewChat?.();
      }
    } catch (error) {
      console.error("[ConversationSidebar] Failed to delete:", error);
    } finally {
      setDeletingId(null);
      setConfirmDeleteSource(null);
    }
  };

  return (
    <>
      <aside
        className="flex-shrink-0 flex flex-col h-full"
        style={{
          width: 280,
          background: "rgba(247, 245, 242, 0.65)",
          borderRight: "1px solid var(--d-rule)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        {/* `.a-newchat` */}
        <button
          type="button"
          onClick={onNewChat}
          className="inline-flex items-center transition-colors"
          style={{
            margin: "18px 14px 10px",
            padding: "9px 12px",
            gap: 8,
            background: "var(--d-surface)",
            color: "var(--d-ink)",
            border: "1px solid var(--d-rule)",
            borderRadius: 10,
            fontFamily: "var(--d-font-sans)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(28,26,22,0.04)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--d-accent-tint)";
            e.currentTarget.style.borderColor = "var(--d-accent-soft)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--d-surface)";
            e.currentTarget.style.borderColor = "var(--d-rule)";
          }}
        >
          <Plus
            className="w-[14px] h-[14px]"
            strokeWidth={ICON_STROKE}
            style={{ color: "var(--d-ink-muted)" }}
          />
          <span>New Chat</span>
        </button>

        {/* `.a-rail-search` */}
        <div
          className="flex items-center"
          style={{
            margin: "0 14px 8px",
            padding: "8px 10px",
            gap: 8,
            background: "var(--d-surface)",
            border: "1px solid var(--d-rule)",
            borderRadius: 10,
            color: "var(--d-ink-ghost)",
          }}
        >
          <Search
            className="w-[13px] h-[13px] flex-shrink-0"
            strokeWidth={ICON_STROKE}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations"
            className="flex-1 min-w-0 bg-transparent border-0 outline-none"
            style={{
              fontFamily: "var(--d-font-body)",
              fontSize: 12.5,
              color: "var(--d-ink)",
            }}
          />
        </div>

        {/* `.a-rail-scroll` */}
        <div className="flex-1 min-h-0 overflow-y-auto d-scroll-sm px-[10px] pb-4">
          {isLoading && (
            <div
              className="px-3 py-4 text-center"
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: 12,
                color: "var(--d-ink-muted)",
              }}
            >
              Loading…
            </div>
          )}

          {!isLoading && conversations.length === 0 && (
            <div
              className="px-3 py-4 text-center"
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: 12,
                color: "var(--d-ink-muted)",
              }}
            >
              {searchQuery ? "No matches" : "No past conversations"}
            </div>
          )}

          {!isLoading &&
            conversations.map((conversation) => {
              const isActive = conversation.id === currentConversationId;
              return (
                <div
                  key={conversation.id}
                  className="group relative flex items-center transition-colors"
                  style={{
                    margin: "0 0 2px",
                    padding: "9px 10px",
                    borderRadius: 8,
                    background: isActive
                      ? "var(--d-surface)"
                      : "transparent",
                    border: isActive
                      ? "1px solid var(--d-rule)"
                      : "1px solid transparent",
                    boxShadow: isActive
                      ? "0 1px 2px rgba(28,26,22,0.04)"
                      : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(255,255,255,0.5)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      onConversationChange?.(
                        conversation.id,
                        conversation.source,
                      )
                    }
                    className="flex-1 flex flex-col text-left min-w-0"
                    disabled={deletingId === conversation.id}
                  >
                    <span
                      className="truncate"
                      style={{
                        fontFamily: "var(--d-font-sans)",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--d-ink)",
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {conversation.title}
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span
                        className="capitalize"
                        style={{
                          fontFamily: "var(--d-font-mono)",
                          fontSize: 10.5,
                          color: "var(--d-ink-ghost)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {conversation.source}
                      </span>
                      <span
                        style={{
                          color: "var(--d-ink-ghost)",
                          opacity: 0.7,
                          fontFamily: "var(--d-font-mono)",
                          fontSize: 10.5,
                        }}
                      >
                        ·
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--d-font-mono)",
                          fontSize: 10.5,
                          color: "var(--d-ink-ghost)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {formatTimestamp(conversation.lastMessageAt)}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) =>
                      handleDeleteClick(
                        conversation.id,
                        conversation.source,
                        e,
                      )
                    }
                    disabled={deletingId === conversation.id}
                    className="opacity-0 group-hover:opacity-100 ml-1 inline-flex items-center justify-center transition-opacity flex-shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--d-neg-soft)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                    aria-label="Delete conversation"
                  >
                    {deletingId === conversation.id ? (
                      <div
                        className="w-3 h-3 rounded-full animate-spin"
                        style={{
                          border: "2px solid var(--d-ink-muted)",
                          borderTopColor: "transparent",
                        }}
                      />
                    ) : (
                      <Trash2
                        className="w-[14px] h-[14px]"
                        strokeWidth={ICON_STROKE}
                        style={{ color: "var(--d-neg-text)" }}
                      />
                    )}
                  </button>
                </div>
              );
            })}
        </div>
      </aside>

      {confirmDeleteId && (
        <PopupModal
          onClose={() => setConfirmDeleteId(null)}
          contentClassName="w-[500px] mx-auto"
          shouldStopPropagation={false}
        >
          <div className="space-y-4">
            <h3
              className="font-semibold"
              style={{
                fontFamily: "var(--d-font-sans)",
                fontSize: 18,
                color: "var(--d-ink)",
                letterSpacing: "-0.01em",
              }}
            >
              Delete Conversation
            </h3>
            <p
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: 13,
                color: "var(--d-ink-muted)",
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to delete this conversation?
            </p>
          </div>
          <div className="flex flex-row gap-3 ml-auto">
            <Button
              variant="outline"
              className="px-6"
              onClick={() => setConfirmDeleteId(null)}
              disabled={deletingId !== null}
            >
              Cancel
            </Button>
            {/* Destructive action — native button styled with kit neg
                tokens. `<Button>` doesn't expose a `style` prop. */}
            <button
              type="button"
              className="d-btn d-btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "var(--d-neg-text)",
                borderColor: "var(--d-neg-text)",
                color: "#fff",
              }}
              onClick={handleConfirmDelete}
              disabled={deletingId !== null}
            >
              {deletingId !== null ? "Deleting…" : "Delete"}
            </button>
          </div>
        </PopupModal>
      )}
    </>
  );
}
